<template>
  <div class="password-update-dialog">
    <!-- 找回密码表单 -->
    <el-form
        ref="forgotFormRef"
        :model="forgotForm"
        :rules="forgotRules"
        class="password-form"
    >
      <el-form-item prop="phone">
        <el-input
            v-model="forgotForm.phone"
            placeholder="请输入手机号"
            size="large"
            class="form-input"
        >
          <template #prefix>
            <el-icon>
              <User/>
            </el-icon>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item prop="verifyCode">
        <el-input
            v-model="forgotForm.verifyCode"
            placeholder="请输入验证码"
            size="large"
            class="form-input"
        >
          <template #prefix>
            <el-icon> <Message /></el-icon>
          </template>
          <template #suffix>
            <el-button
                link
                class="captcha-btn"
                :disabled="countdown > 0"
                @click="sendVerifyCode"
            >
              {{ countdown > 0 ? `${countdown}秒后重试` : '获取验证码' }}
            </el-button>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item prop="newPassword">
        <el-input
            v-model="forgotForm.newPassword"
            type="password"
            placeholder="请输入新密码（至少8位）"
            size="large"
            class="form-input"
            show-password
        >
          <template #prefix>
            <el-icon>
              <Lock/>
            </el-icon>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item prop="confirmPassword">
        <el-input
            v-model="forgotForm.confirmPassword"
            type="password"
            placeholder="请确认新密码"
            size="large"
            class="form-input"
            show-password
        >
          <template #prefix>
            <el-icon>
              <Lock/>
            </el-icon>
          </template>
        </el-input>
      </el-form-item>

      <div class="dialog-actions">
        <el-button
            @click="handleCancel"
            class="cancel-btn"
            size="large"
        >
          取消
        </el-button>
        <el-button
            type="primary"
            @click="handleForgotPassword"
            class="submit-btn"
            size="large"
        >
          重置密码
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import request from '@/utils/request'

export default {
  name: "PasswordUpdateDialog",
  emits: ['success', 'cancel'],

  setup(props, { emit }) {
    // 响应式数据
    const countdown = ref(0)
    const timer = ref(null)

    // 忘记密码表单
    const forgotForm = reactive({
      phone: '',
      verifyCode: '',
      newPassword: '',
      confirmPassword: ''
    })

    // 表单引用
    const forgotFormRef = ref()

    // 表单规则
    const forgotRules = {
      phone: [
        {required: true, message: '请输入手机号', trigger: 'blur'},
        {pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur'}
      ],
      verifyCode: [
        {required: true, message: '请输入验证码', trigger: 'blur'}
      ],
      newPassword: [
        {required: true, message: '请输入新密码', trigger: 'blur'},
        {min: 8, message: '密码长度至少8位', trigger: 'blur'}
      ],
      confirmPassword: [
        {required: true, message: '请确认新密码', trigger: 'blur'},
        {
          validator: (rule, value, callback) => {
            if (value !== forgotForm.newPassword) {
              callback(new Error('两次输入的密码不一致'))
            } else {
              callback()
            }
          },
          trigger: 'blur'
        }
      ]
    }

    // 发送验证码
    const sendVerifyCode = async () => {
      if (!forgotForm.phone) {
        ElMessage.error('请输入手机号')
        return
      }

      if (!/^1[3-9]\d{9}$/.test(forgotForm.phone)) {
        ElMessage.error('请输入正确的手机号')
        return
      }

      try {
        await request.get('/auth/verify-code/phone', {
          params: {
            phone: forgotForm.phone,
            scene: 'Bind'
          }
        })

        ElMessage.success('验证码发送成功')
        startCountdown()
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '验证码发送失败')
      }
    }

    // 开始倒计时
    const startCountdown = () => {
      countdown.value = 60
      timer.value = setInterval(() => {
        countdown.value--
        if (countdown.value <= 0) {
          clearInterval(timer.value)
        }
      }, 1000)
    }

    // 忘记密码提交
    const handleForgotPassword = async () => {
      if (!forgotFormRef.value) return

      try {
        await forgotFormRef.value.validate()

        const {confirmPassword, ...submitData} = forgotForm
        await request.post('/auth/forgot-password', submitData)

        // ElMessage.success('密码重置成功')
        emit('success')
      } catch (error) {
        if (error.errors) {
          // 表单验证错误
          return
        }
        ElMessage.error(error.response?.data?.message || '密码重置失败')
      }
    }

    // 取消操作
    const handleCancel = () => {
      emit('cancel')
    }

    // 清理定时器
    const clearTimer = () => {
      if (timer.value) {
        clearInterval(timer.value)
      }
    }

    onUnmounted(() => {
      clearTimer()
    })

    return {
      countdown,
      forgotForm,
      forgotFormRef,
      forgotRules,
      sendVerifyCode,
      handleForgotPassword,
      handleCancel,
      clearTimer
    }
  }
}
</script>

<style scoped lang="scss">
.password-update-dialog {
  .password-form {
    min-height: 220px;
    margin: 0 !important;
    flex-direction: column;
    justify-content: flex-start;
  }

  /* 输入框样式 */
  .form-input {
    :deep(.el-input__wrapper) {
      border-radius: 0;
      padding: 1px 12px;
      box-shadow: 0 0 0 1px #dcdfe6 inset;
      height: 38px;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 0 0 1px #c0c4cc inset;
      }

      &.is-focus {
        box-shadow: 0 0 0 1px #409EFF inset;
      }
    }

    :deep(.el-input__prefix) {
      left: 8px;
      color: #606266;
    }

    :deep(.el-input__suffix) {
      display: flex;
      align-items: center;
      padding-right: 8px;
    }

    :deep(.el-input__inner) {
      padding-right:90px;
    }

    .captcha-btn {
      padding: 0;
      height: auto;
      color: #373737;
      font-size: 13px;
      white-space: nowrap;
      margin-left: 4px;

      &:hover {
        color: #66b1ff;
      }

      &:disabled {
        color: #c0c4cc;
        cursor: not-allowed;
      }
    }

    :deep(input) {
      font-size: 14px;
      height: 16px;
      line-height: 36px;

      &:-webkit-autofill,
      &:-webkit-autofill:hover,
      &:-webkit-autofill:focus,
      &:-webkit-autofill:active {
        -webkit-text-fill-color: #606266 !important;
        -webkit-box-shadow: 0 0 0px 1000px white inset !important;
        transition: background-color 5000s ease-in-out 0s;
      }
    }
  }

  .dialog-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 20px;
  }

  .cancel-btn {
    width: 100px;
    border-radius: 0;
  }

  .submit-btn {
    width: 100px;
    border-radius: 0;
    padding: 10px 16px;
    font-weight: 600;
    font-size: 14px;
    transition: all 0.3s;
    box-shadow: 0 3px 8px rgba(64, 158, 255, 0.3);
    height: 42px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 10px rgba(64, 158, 255, 0.4);
    }
  }
}
</style>